import type { QueryClient } from "@tanstack/react-query";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";

/**
 * Same API path as DocumentItem.handleUpdateTopics for a single topic selection.
 */
export async function linkDocumentToTopicAfterCreate(
    documentId: string,
    topicId: string,
    spaceId: string | null
): Promise<void> {
    await knowledgeBaseService.updateKnowledgeBaseItem(
        documentId,
        { topicIds: [topicId] },
        spaceId ?? null
    );
}

/** Invalidate/refetch KB and topic-related queries after topic link updates (aligned with DocumentItem). */
export function invalidateAfterDocumentTopicLink(
    queryClient: QueryClient,
    spaceId: string | null | undefined,
    onRefresh?: () => void
): void {
    void queryClient.refetchQueries({ queryKey: ["/api/knowledgebase"], exact: false });
    if (spaceId) {
        queryClient.invalidateQueries({ queryKey: ["/api/spaces", spaceId, "topics"] });
    }
    queryClient.invalidateQueries({ queryKey: ["/api/topics"] });
    queryClient.invalidateQueries({ queryKey: ["knowledgebase-summary"] });
    onRefresh?.();
}

/**
 * Link multiple documents to one topic, then run a single refetch/invalidate pass.
 */
export async function linkDocumentsToTopicAfterCreate(
    documentIds: string[],
    topicId: string,
    spaceId: string | null,
    queryClient: QueryClient,
    onRefresh?: () => void
): Promise<void> {
    for (const id of documentIds) {
        await linkDocumentToTopicAfterCreate(id, topicId, spaceId);
    }
    invalidateAfterDocumentTopicLink(queryClient, spaceId, onRefresh);
}

export function invalidateKnowledgeBaseListQueries(queryClient: QueryClient): void {
    queryClient.invalidateQueries({
        predicate: (query) => {
            const key = query.queryKey[0];
            return (
                typeof key === "string" &&
                (key === "/api/knowledge-base" ||
                    key === "/api/knowledgebase" ||
                    key === "knowledgebase" ||
                    key === "knowledgebase-summary")
            );
        },
    });
}
